import Tracking from './tracking.js';
import { TRACKING_EVENTS } from './tracking-constants.js';

const PopupManager = {
    registeredPopups: new Map(),

    /**
     * Register popup configuration
     * @param {string} popupType
     * @param {Object} config
     */
    register(popupType, config = {}) {
        if (!popupType) {
            return;
        }

        this.registeredPopups.set(popupType, { ...config });
    },

    /**
     * Retrieve popup configuration
     * @param {string} popupType
     * @returns {Object}
     */
    getPopupConfig(popupType) {
        return this.registeredPopups.get(popupType) || {};
    },

    /**
     * Initialize popup manager
     */
    init() {
        this.setupTriggers();
    },

    /**
     * Attach event listeners to popup triggers
     */
    setupTriggers() {
        const triggers = document.querySelectorAll('[data-popup]');

        triggers.forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                event.preventDefault();

                const popupType = trigger.getAttribute('data-popup');
                if (!popupType) {
                    return;
                }

                const typeConfig = this.getPopupConfig(popupType);
                const attributeOptions = this.buildAttributeOptions(trigger);
                const finalOptions = this.prepareOptions(popupType, trigger, typeConfig, attributeOptions);

                const popupContentElement = document.getElementById(`${popupType}-popup-content`);

                if (popupContentElement) {
                    this.openPopupFromHTML(`#${popupType}-popup-content`, finalOptions);
                } else {
                    this.openPopup(popupType, trigger, finalOptions);
                }
            });
        });
    },

    /**
     * Build popup options from trigger data attributes
     * @param {HTMLElement} trigger
     * @returns {Object}
     */
    buildAttributeOptions(trigger) {
        const options = {};

        const size = trigger.getAttribute('data-popup-size');
        const width = trigger.getAttribute('data-popup-width');
        const customClass = trigger.getAttribute('data-popup-class');
        const bodyClass = trigger.getAttribute('data-popup-body-class');
        const large = trigger.hasAttribute('data-popup-large');

        if (size) {
            options.size = size;
        }

        if (width) {
            options.width = width;
        }

        if (customClass) {
            options.className = customClass;
        }

        if (bodyClass) {
            options.bodyClasses = bodyClass;
        }

        if (large) {
            options.large = true;
        }

        return options;
    },

    /**
     * Prepare final popup options by merging configuration sources
     * @param {string} popupType
     * @param {HTMLElement} trigger
     * @param {Object} typeConfig
     * @param {Object} attributeOptions
     * @returns {Object}
     */
    prepareOptions(popupType, trigger, typeConfig = {}, attributeOptions = {}) {
        const combined = {
            popupType,
            trigger, // Store trigger for use in onOpen callback
            ...typeConfig,
            ...attributeOptions,
        };

        combined.className = this.mergeClassNames(typeConfig.className, attributeOptions.className);
        combined.bodyClasses = this.mergeClassNames(typeConfig.bodyClasses, attributeOptions.bodyClasses);

        const typeOnOpen = typeof typeConfig.onOpen === 'function' ? typeConfig.onOpen : null;
        const attrOnOpen = typeof attributeOptions.onOpen === 'function' ? attributeOptions.onOpen : null;
        const shouldInitForm = typeConfig.skipDefaultFormInit !== true;

        combined.onOpen = (container) => {
            if (shouldInitForm) {
                this.initializePopupForm(container, popupType, trigger, typeConfig);
            }

            if (typeOnOpen) {
                typeOnOpen(container, trigger, popupType);
            }

            if (attrOnOpen) {
                attrOnOpen(container, trigger, popupType);
            }
        };

        if (typeof typeConfig.onClose === 'function') {
            combined.onClose = typeConfig.onClose;
        }

        return combined;
    },

    /**
     * Merge class name inputs into a unique array
     * @param  {...(string|string[])} classValues
     * @returns {string[]|null}
     */
    mergeClassNames(...classValues) {
        const classes = new Set();

        classValues.forEach((value) => {
            if (!value) {
                return;
            }

            if (Array.isArray(value)) {
                value.forEach((cls) => cls && classes.add(cls));
            } else if (typeof value === 'string') {
                value
                    .split(' ')
                    .map((cls) => cls.trim())
                    .filter(Boolean)
                    .forEach((cls) => classes.add(cls));
            }
        });

        const result = Array.from(classes);
        return result.length > 0 ? result : null;
    },

    /**
     * Generic popup opener
     */
    openPopup(popupType, trigger = null, options = {}) {
        const existingPopup = document.getElementById('popup-manager');
        if (existingPopup) {
            existingPopup.remove();
        }

        const popupOverlay = document.createElement('div');
        popupOverlay.id = 'popup-manager';
        popupOverlay.className = 'popup';

        const popupContent = document.createElement('div');
        popupContent.className = 'popup__content';
        popupContent.classList.add(`popup__content--${popupType}`);

        if (options.large) {
            popupContent.classList.add('popup__content--large');
        }

        if (options.size) {
            popupContent.classList.add(`popup__content--${options.size}`);
        }

        if (options.width) {
            popupContent.style.maxWidth = options.width;
        }

        const classNames = this.mergeClassNames(options.className);
        // Check if this is a bottom-sheet popup
        const isBottomSheet = classNames && classNames.includes('popup__content--bottom-sheet');
        
        if (classNames) {
            classNames.forEach((cls) => popupContent.classList.add(cls));
            
            // Add bottom-sheet overlay class if content has bottom-sheet class
            if (isBottomSheet) {
                popupOverlay.classList.add('popup--bottom-sheet-overlay');
            }
        }

        const closeButton = document.createElement('button');
        closeButton.className = 'popup__close';
        closeButton.setAttribute('aria-label', 'Close popup');

        const closeIcon = document.createElement('img');
        closeIcon.src = `${window.location.origin}/wp-content/themes/pe-mp-theme/dist/icons/icon-menu-close.svg`;
        closeIcon.alt = 'Close';
        closeButton.appendChild(closeIcon);
        
        // Create header with drag handle for bottom-sheet modals
        if (isBottomSheet) {
            const header = document.createElement('div');
            header.className = 'popup__header';
            header.setAttribute('role', 'button');
            header.setAttribute('aria-label', 'Close popup');
            header.setAttribute('tabindex', '0');
            
            const dragHandle = document.createElement('div');
            dragHandle.className = 'popup__header-handle';
            header.appendChild(dragHandle);
            
            // Make header clickable to close
            header.addEventListener('click', () => {
                const popup = document.getElementById('popup-manager');
                if (popup) {
                    this.closePopup(popup);
                }
            });
            
            // Support keyboard navigation
            header.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const popup = document.getElementById('popup-manager');
                    if (popup) {
                        this.closePopup(popup);
                    }
                }
            });
            
            popupContent.appendChild(header);
        }

        const contentContainer = document.createElement('div');
        contentContainer.className = 'popup__body';

        const bodyClasses = this.mergeClassNames(options.bodyClasses);
        if (bodyClasses) {
            bodyClasses.forEach((cls) => contentContainer.classList.add(cls));
        }

        if (popupType === 'feedback') {
            this.createFeedbackPopupContent(contentContainer, options);
        } else {
            this.createGenericPopupContent(contentContainer, popupType, trigger, options, isBottomSheet);
        }

        popupContent.appendChild(closeButton);
        popupContent.appendChild(contentContainer);
        popupOverlay.appendChild(popupContent);
        document.body.appendChild(popupOverlay);

        this.setupPopupEventListeners(popupOverlay, closeButton, options);
        this.setupFocusManagement(popupOverlay);

        Tracking.trackStandardEvent(TRACKING_EVENTS.ENGAGEMENT.POPUP_OPENED, {
            popup_type: popupType,
            trigger_element: trigger ? trigger.tagName : 'programmatic'
        });
    },

    /**
     * Create generic popup content
     */
    createGenericPopupContent(container, popupType, trigger, options, isBottomSheet = false) {
        const content = options.content || 'Popup content';

        // Only add title if not a bottom-sheet popup
        if (!isBottomSheet) {
            const title = options.title || 'Popup';
            const titleElement = document.createElement('h2');
            titleElement.className = 'popup__title';
            titleElement.textContent = title;
            container.appendChild(titleElement);
        }

        const contentElement = document.createElement('div');
        contentElement.innerHTML = content;

        container.appendChild(contentElement);
    },

    /**
     * Create feedback popup content
     */
    createFeedbackPopupContent(container, options) {
        const title = options.title || 'Feedback';
        const content = options.content || '';

        const titleElement = document.createElement('h2');
        titleElement.className = 'popup__title';
        titleElement.textContent = title;

        const contentElement = document.createElement('div');
        contentElement.innerHTML = content;

        container.appendChild(titleElement);
        container.appendChild(contentElement);

        const form = contentElement.querySelector('form');
        if (form) {
            form.addEventListener('submit', (e) => {
                e.preventDefault();

                const formData = new FormData(form);
                const data = {};
                for (const [key, value] of formData.entries()) {
                    data[key] = value;
                }

                const textarea = form.querySelector('textarea');
                if (textarea) {
                    data.feedback = textarea.value.trim();
                }

                if (textarea && !data.feedback) {
                    alert('Please provide some feedback before submitting.');
                    return;
                }

                if (options.onSubmit) {
                    options.onSubmit(data);
                }

                const popup = document.getElementById('popup-manager');
                if (popup) {
                    popup.remove();
                }
            });
        }
    },

    /**
     * Setup popup event listeners
     */
    setupPopupEventListeners(popupOverlay, closeButton, options) {
        closeButton.addEventListener('click', () => {
            this.closePopup(popupOverlay, options);
        });

        popupOverlay.addEventListener('click', (e) => {
            if (e.target === popupOverlay) {
                this.closePopup(popupOverlay, options);
            }
        });

        const escapeHandler = (e) => {
            if (e.key === 'Escape') {
                this.closePopup(popupOverlay, options);
                document.removeEventListener('keydown', escapeHandler);
            }
        };

        document.addEventListener('keydown', escapeHandler);
    },

    /**
     * Setup focus management for accessibility
     */
    setupFocusManagement(popupOverlay) {
        const focusableElements = popupOverlay.querySelectorAll(
            'input, button, textarea, select, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length > 0) {
            focusableElements[0].focus();
        }

        const trapFocus = (e) => {
            if (e.key === 'Tab') {
                const elements = popupOverlay.querySelectorAll(
                    'input, button, textarea, select, [tabindex]:not([tabindex="-1"])'
                );

                if (!elements.length) {
                    return;
                }

                const firstElement = elements[0];
                const lastElement = elements[elements.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                }
            }
        };

        popupOverlay.addEventListener('keydown', trapFocus);
    },

    /**
     * Close popup
     */
    closePopup(popupOverlay, options = {}) {
        if (popupOverlay && popupOverlay.parentNode) {
            popupOverlay.remove();

            Tracking.trackStandardEvent(TRACKING_EVENTS.ENGAGEMENT.POPUP_CLOSED, {
                popup_type: options.popupType || 'generic',
                close_method: 'user_action'
            });

            if (options.onClose && typeof options.onClose === 'function') {
                options.onClose();
            }
        }
    },

    /**
     * Close all popups
     */
    closeAllPopups() {
        const popups = document.querySelectorAll('.popup');
        popups.forEach((popup) => {
            popup.remove();
        });
    },

    /**
     * Open popup from existing HTML element
     * @param {string|HTMLElement} elementOrSelector
     * @param {Object} options
     */
    openPopupFromHTML(elementOrSelector, options = {}) {
        const existingPopup = document.getElementById('popup-manager');
        if (existingPopup) {
            existingPopup.remove();
        }

        const sourceElement =
            typeof elementOrSelector === 'string'
                ? document.querySelector(elementOrSelector)
                : elementOrSelector;

        if (!sourceElement) {
            console.error('Popup source element not found:', elementOrSelector);
            return;
        }

        const popupOverlay = document.createElement('div');
        popupOverlay.id = 'popup-manager';
        popupOverlay.className = 'popup';

        const popupContent = document.createElement('div');
        popupContent.className = 'popup__content';

        if (options.popupType) {
            popupContent.classList.add(`popup__content--${options.popupType}`);
        }

        if (options.large) {
            popupContent.classList.add('popup__content--large');
        }

        if (options.size) {
            popupContent.classList.add(`popup__content--${options.size}`);
        }

        if (options.width) {
            popupContent.style.maxWidth = options.width;
        }

        const classNames = this.mergeClassNames(options.className);
        if (classNames) {
            classNames.forEach((cls) => popupContent.classList.add(cls));
        }

        const closeButton = document.createElement('button');
        closeButton.className = 'popup__close';
        closeButton.setAttribute('aria-label', 'Close popup');

        const closeIcon = document.createElement('img');
        closeIcon.src = `${window.location.origin}/wp-content/themes/pe-mp-theme/dist/icons/icon-menu-close.svg`;
        closeIcon.alt = 'Close';
        closeButton.appendChild(closeIcon);

        const contentContainer = document.createElement('div');
        contentContainer.className = 'popup__body';

        if (options.popupType) {
            contentContainer.classList.add(`popup__body--${options.popupType}`);
        }

        const bodyClasses = this.mergeClassNames(options.bodyClasses);
        if (bodyClasses) {
            bodyClasses.forEach((cls) => contentContainer.classList.add(cls));
        }

        contentContainer.innerHTML = sourceElement.innerHTML;

        popupContent.appendChild(closeButton);
        popupContent.appendChild(contentContainer);
        popupOverlay.appendChild(popupContent);
        document.body.appendChild(popupOverlay);

        this.setupPopupEventListeners(popupOverlay, closeButton, options);
        this.setupFocusManagement(popupOverlay);

        Tracking.trackStandardEvent(TRACKING_EVENTS.ENGAGEMENT.POPUP_OPENED, {
            popup_type: options.popupType || 'html',
            trigger_element: 'programmatic'
        });

        setTimeout(() => {
            if (window.InputLabels && typeof window.InputLabels.init === 'function') {
                window.InputLabels.init();
            }
        }, 100);

        if (options.onOpen && typeof options.onOpen === 'function') {
            setTimeout(() => {
                options.onOpen(contentContainer);
            }, 150);
        }
    },

    /**
     * Initialize form handlers for popup forms
     * @param {HTMLElement} container
     * @param {string} popupType
     * @param {HTMLElement} trigger
     * @param {Object} typeConfig
     */
    initializePopupForm(container, popupType, trigger, typeConfig = {}) {
        const form = container.querySelector('form');
        if (!form) {
            return;
        }

        const preserveFormIds = typeConfig.preserveFormIds === true;

        if (!preserveFormIds) {
            const uniqueId = `${popupType}-form-${Date.now()}`;
            form.id = uniqueId;

            const inputs = form.querySelectorAll('input, textarea, select');
            inputs.forEach((input) => {
                if (input.id) {
                    const label = form.querySelector(`label[for="${input.id}"]`);
                    const newId = `${input.id}-${Date.now()}`;
                    input.id = newId;
                    if (label) {
                        label.setAttribute('for', newId);
                    }
                }
            });
        }

        if (!form.hasAttribute('data-form-type')) {
            if (popupType === 'provider-contact') {
                form.setAttribute('data-form-type', 'provider-contact');
            } else if (typeConfig.defaultFormType) {
                form.setAttribute('data-form-type', typeConfig.defaultFormType);
            }
        }

        if (popupType === 'provider-contact') {
            this.initializeProviderContactForm(form, container, trigger);
        }

        if (window.FormSubmission && typeof window.FormSubmission.setupFormHandler === 'function') {
            window.FormSubmission.setupFormHandler(form);
        }

        if (typeof typeConfig.onFormReady === 'function') {
            typeConfig.onFormReady(form, container, popupType, trigger);
        }
    },

    /**
     * Initialize provider contact form
     * Populates form fields from trigger button data attributes
     * @param {HTMLElement} form
     * @param {HTMLElement} container
     * @param {HTMLElement} trigger
     */
    initializeProviderContactForm(form, container, trigger) {
        if (!trigger) {
            return;
        }

        // Get provider data from trigger button data attributes
        const providerId = trigger.getAttribute('data-provider-id');
        const providerName = trigger.getAttribute('data-provider-name');
        const providerCountry = trigger.getAttribute('data-provider-country');
        const providerCity = trigger.getAttribute('data-provider-city');
        const providerType = trigger.getAttribute('data-provider-type');
        const providerImage = trigger.getAttribute('data-provider-image');

        let providerUrl = trigger.getAttribute('data-provider-url') || '';
        if (!providerUrl && providerId) {
            const card = trigger.closest('.card-v2, .provider-single__card');
            const providerLink = card?.querySelector('a[href*="/providers/"]');
            if (providerLink?.href) {
                providerUrl = providerLink.href;
            } else {
                providerUrl = `${window.location.origin}/providers/${providerId}/`;
            }
        }

        // Update hidden form fields
        const providerIdField = form.querySelector('[data-provider-id-field]');
        const providerUrlField = form.querySelector('[data-provider-url-field]');
        const providerNameField = form.querySelector('[data-provider-name-field]');

        if (providerIdField && providerId) {
            providerIdField.value = providerId;
        }
        if (providerUrlField && providerUrl) {
            providerUrlField.value = providerUrl;
        }
        if (providerNameField && providerName) {
            providerNameField.value = providerName;
        }

        // Update visible fields in the meta section
        const titleField = container.querySelector('[data-provider-title-field]');
        const countryField = container.querySelector('[data-provider-country-field]');
        const imageField = container.querySelector('[data-provider-image-field]');

        if (titleField && providerName) {
            titleField.textContent = providerName;
        }

        if (countryField) {
            if (providerCountry) {
                // Show country field and update content
                countryField.style.display = '';
                // Only update if field is empty or doesn't already contain the country
                const currentText = countryField.textContent.trim();
                if (!currentText || !currentText.includes(providerCountry)) {
                    // Check if there's already flag HTML (from initial render)
                    const hasFlag = countryField.querySelector('img, span') || 
                                   countryField.innerHTML.includes('flag') ||
                                   countryField.innerHTML.includes('🇺🇸') || // emoji flags
                                   countryField.innerHTML.match(/[\u{1F1E6}-\u{1F1FF}]{2}/u); // flag emoji pattern
                    
                    if (hasFlag) {
                        // Keep existing flag, just update text after it
                        const textNode = Array.from(countryField.childNodes).find(node => 
                            node.nodeType === Node.TEXT_NODE && node.textContent.trim()
                        );
                        if (textNode) {
                            textNode.textContent = ' ' + providerCountry;
                        } else {
                            countryField.appendChild(document.createTextNode(' ' + providerCountry));
                        }
                    } else {
                        // No flag, just set country name
                        countryField.textContent = providerCountry;
                    }
                }
            } else {
                countryField.style.display = 'none';
            }
        }

        // Update message textarea placeholder
        const messageField = form.querySelector('textarea[name="message"]');
        if (messageField && providerName) {
            const placeholderTemplate = messageField.getAttribute('data-message-placeholder-template');
            if (placeholderTemplate) {
                const placeholder = placeholderTemplate.replace('{provider_name}', providerName);
                messageField.setAttribute('placeholder', placeholder);
            } else {
                messageField.setAttribute('placeholder', 
                    `I am interested in getting treatment at ${providerName}, my primary need is...`);
            }
        }

        // Try to get provider image from data attribute first, then from card
        if (imageField && trigger) {
            let imageUrl = null;
            
            // First, try to get image from data-provider-image attribute
            if (providerImage) {
                imageUrl = providerImage;
            } else {
                // Fallback: try to get image from card
                const card = trigger.closest('.card-v2, .provider-single__card');
                if (card) {
                    const cardImage = card.querySelector('img');
                    if (cardImage && cardImage.src) {
                        imageUrl = cardImage.src;
                    }
                }
            }
            
            // If we found an image, update the field
            if (imageUrl) {
                imageField.src = imageUrl;
                imageField.alt = providerName || 'Provider';
                imageField.style.display = '';
            }
        }
    },
};

export default PopupManager;

window.PopupManager = PopupManager;

